import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import SuperFetch from 'library/helpers/superFetch';
import { Notification } from 'zComponents/atoms';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { assignCredential } from 'reduxStore/credentials/saga';
import { roomStaysActions } from './slice';
import { selectRoomStaysEnabled } from './selectors';

// Never the raw server exception text on a 5xx (DB1-06): the shared helper decides what is safe to show.
const errMsg = (res, fallback) => apiFailureMessage(res, fallback);

/** A fresh idempotency key per staff action (C1): a retry of the same click reuses it, a new click gets a new one. */
export const newOperationKey = () =>
	`dash-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;

// GET /room-stays/holders — only when the flag is on; otherwise the endpoint is 403 and there is nothing to show.
export function* loadHolders() {
	try {
		if (!(yield select(selectRoomStaysEnabled))) {
			yield put(roomStaysActions.loadHoldersSuccess([]));
			return;
		}
		// Retired generated holders too, so lists keep hiding them after retirement (§5.1.2); selectRoomHolders drops
		// them for everything that acts on a holder.
		const res = yield call(() => SuperFetch.get('/room-stays/holders', {}, { includeRetiredGenerated: true }));
		if (res.statusCode === 200) yield put(roomStaysActions.loadHoldersSuccess(res.data || []));
		else throw Error(errMsg(res, 'Failed to load room holders.'));
	} catch (error) {
		yield put(roomStaysActions.loadHoldersError(error.message));
	}
}

// GET /room-stays/rooms/:holderID/stays
export function* loadStays({ payload: { holderID } }) {
	try {
		// Current and upcoming only: stays that end after now (DA1-08). An explicit-offset instant, as the server requires.
		const from = new Date().toISOString();
		const res = yield call(() => SuperFetch.get(`/room-stays/rooms/${holderID}/stays`, {}, { from }));
		if (res.statusCode === 200) yield put(roomStaysActions.loadStaysSuccess({ holderID, stays: res.data || [] }));
		else throw Error(errMsg(res, 'Failed to load stays.'));
	} catch (error) {
		yield put(roomStaysActions.loadStaysError(error.message));
	}
}

/**
 * Change a stay at its revision (R13): `{ endAt }` extends or shortens, `{ cancel: true }` cancels. The end is an
 * explicit-offset instant built in the stay's own zone by the caller. A revision conflict reloads so staff see why.
 */
export function* changeStay({ payload: { holderID, roomStayID, revision, endAt, cancel = false, operationKey } }) {
	try {
		const body = { expectedRevision: revision, operationKey: operationKey || newOperationKey() };
		const res = yield call(() =>
			cancel
				? SuperFetch.post(`/room-stays/stays/${roomStayID}/cancel`, body)
				: SuperFetch.patch(`/room-stays/stays/${roomStayID}`, { ...body, endAt })
		);
		if (res.statusCode === 200) {
			const pending = res.data && (res.data.hardwareState === 'pending' || res.data.hardwareState === 'blocked');
			Notification(
				pending ? 'warning' : 'success',
				cancel ? 'Stay cancelled' : 'Stay updated',
				pending
					? 'The room locks are still being updated; the old window may apply until they confirm.'
					: undefined
			);
			yield put(roomStaysActions.changeStayDone({ roomStayID, operation: res.data || null }));
		} else {
			throw Error(errMsg(res, 'The stay could not be changed.'));
		}
	} catch (error) {
		yield put(roomStaysActions.changeStayDone({ roomStayID, error: error.message }));
		Notification('error', error.message);
	}
	yield put(roomStaysActions.loadStays({ holderID }));
}

/**
 * Add a stay by hand (POST /room-stays/stays, the fallback adapter). `startAt`/`endAt` are explicit-offset instants in
 * the org's zone. With `thenAssign`, the card staff were issuing is re-sent once the stay exists, so the server answers
 * with the new stay to confirm (R15) instead of NO_APPLICABLE_STAY.
 */
export function* createStay({
	payload: { holderID, visitor, bookingReference = null, startAt, endAt, operationKey, thenAssign = null }
}) {
	try {
		const res = yield call(() =>
			SuperFetch.post('/room-stays/stays', {
				cardPoolHolderID: holderID,
				visitor,
				...(bookingReference ? { bookingReference } : {}),
				startAt,
				endAt,
				operationKey: operationKey || newOperationKey()
			})
		);
		if (res.statusCode !== 200 && res.statusCode !== 201) throw Error(errMsg(res, 'The stay could not be added.'));
		Notification('success', 'Stay added');
		yield put(roomStaysActions.createStayDone());
		if (thenAssign) {
			yield put(
				credentialsActions.assignCredential({
					credentialID: thenAssign.credentialID,
					data: { newPersonID: thenAssign.newPersonID }
				})
			);
		}
	} catch (error) {
		yield put(roomStaysActions.createStayDone({ error: error.message }));
		Notification('error', error.message);
	}
	yield put(roomStaysActions.loadStays({ holderID }));
}

/**
 * Manage Access "Hotel guest" mode, after staff reviewed one stay and its cards. The stay is saved first: a new one is
 * created, a changed one is PATCHed at the revision staff reviewed (a conflict stops here; nothing is issued against a
 * stay someone else changed). Each card is then assigned to the room with that stay's ID and revision, in turn, through
 * the same assign the People mode uses, so the server's checks, the queued confirmations, a stale stay's fresh preview
 * and the accepted-versus-delivered notice all behave exactly as they do there.
 */
export function* issueForStay({
	payload: { holderID, roomPersonID, stay, newStay, change, credentialIDs, operationKey }
}) {
	try {
		let bound = stay ? { roomStayID: stay.roomStayID, revision: stay.revision } : null;
		if (newStay) {
			const res = yield call(() =>
				SuperFetch.post('/room-stays/stays', {
					cardPoolHolderID: holderID,
					visitor: newStay.visitor,
					...(newStay.bookingReference ? { bookingReference: newStay.bookingReference } : {}),
					startAt: newStay.startAt,
					endAt: newStay.endAt,
					operationKey
				})
			);
			if (res.statusCode !== 200 && res.statusCode !== 201)
				throw Error(errMsg(res, 'The stay could not be added.'));
			bound = { roomStayID: res.data.roomStayID, revision: res.data.revision };
		} else if (change) {
			const res = yield call(() =>
				SuperFetch.patch(`/room-stays/stays/${stay.roomStayID}`, {
					expectedRevision: stay.revision,
					operationKey,
					...change
				})
			);
			if (res.statusCode !== 200) throw Error(errMsg(res, 'The stay dates could not be changed.'));
			bound = { roomStayID: res.data.roomStayID ?? stay.roomStayID, revision: res.data.revision };
		}
		if (!bound?.roomStayID || bound.revision == null) throw Error('The stay could not be confirmed. Try again.');
		for (const credentialID of credentialIDs) {
			yield call(assignCredential, {
				payload: { credentialID, data: { newPersonID: roomPersonID, roomStay: bound } }
			});
		}
		yield put(roomStaysActions.issueForStayDone({ ok: true, roomStayID: bound.roomStayID }));
	} catch (error) {
		yield put(roomStaysActions.issueForStayDone({ ok: false, error: error.message }));
		Notification('error', error.message);
	}
	yield put(roomStaysActions.loadStays({ holderID }));
}

// ─── Room stays page (room-cards-setup PLAN.md §5.2, rows 9 and 11a) ─────────────────────────────────────────────
// Each saga refuses to send while the flag is off, so a stale tab or deep link can never reach the server (INV-01).

/** The refusal a route sent (`data.reason`) with its message, or the generic failure text. */
export const refusalOf = (res, fallback) => ({ code: res?.data?.reason ?? null, message: errMsg(res, fallback) });

const flagOn = function* () {
	return Boolean(yield select(selectRoomStaysEnabled));
};

// GET /room-stays/board/:siteID — delivery state is the server's, shown as sent (INV-10).
export function* loadBoard({ payload: { siteID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.get(`/room-stays/board/${siteID}`));
	if (res.statusCode === 200) yield put(roomStaysActions.loadBoardDone({ siteID, data: res.data }));
	else
		yield put(
			roomStaysActions.loadBoardDone({ siteID, error: errMsg(res, 'The rooms board could not be loaded.') })
		);
}

// All sites (PLAN v0.8.5 §5.2): each site's own GET board/:siteID, in parallel. Both that route and GET holders (where
// the site list comes from) are site-scoped on the server, so this adds no reach. One request per site, not one
// loadBoard each: the board loads share one takeLatest, so a burst of them would cancel all but the last.
export function* loadBoards({ payload: { siteIDs = [] } }) {
	if (!(yield* flagOn())) return;
	const results = yield all(siteIDs.map((siteID) => call(() => SuperFetch.get(`/room-stays/board/${siteID}`))));
	const boards = [];
	const errors = [];
	results.forEach((res, i) => {
		if (res?.statusCode === 200) boards.push({ siteID: siteIDs[i], data: res.data });
		else errors.push(errMsg(res, 'A site’s rooms could not be loaded.'));
	});
	yield put(roomStaysActions.loadBoardsDone({ boards, errors: [...new Set(errors)] }));
}

function* loadAnyBoard(action) {
	if (action.type === roomStaysActions.loadBoards.type) yield* loadBoards(action);
	else yield* loadBoard(action);
}

// GET /room-stays/setup/:siteID (404 = not set up yet is impossible: a known site answers with empty holders).
export function* loadSetup({ payload: { siteID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.get(`/room-stays/setup/${siteID}`));
	if (res.statusCode === 200) yield put(roomStaysActions.loadSetupDone({ siteID, data: res.data }));
	else
		yield put(
			roomStaysActions.loadSetupDone({ siteID, error: errMsg(res, 'The site setup could not be loaded.') })
		);
}

// GET /room-stays/setup/:siteID/occupancy — the latest stored reading per Base Building lock (R5a).
export function* loadOccupancy({ payload: { siteID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.get(`/room-stays/setup/${siteID}/occupancy`));
	if (res.statusCode === 200) yield put(roomStaysActions.occupancyDone({ siteID, locks: res.data || [] }));
	else
		yield put(
			roomStaysActions.occupancyDone({ siteID, error: errMsg(res, 'Lock occupancy could not be loaded.') })
		);
}

// POST /room-stays/setup/:siteID/occupancy/read — read every Base Building lock now, then show the stored readings.
export function* readOccupancy({ payload: { siteID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.post(`/room-stays/setup/${siteID}/occupancy/read`, {}));
	if (res.statusCode !== 200) {
		const { message } = refusalOf(res, 'The locks could not be read.');
		yield put(roomStaysActions.occupancyDone({ siteID, error: message }));
		Notification('error', message);
		return;
	}
	yield call(loadOccupancy, { payload: { siteID } });
}

// GET /room-stays/setup/:siteID/panel-users — synced Integriti people; `eligible` is the person half only.
export function* loadPanelUsers({ payload: { siteID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.get(`/room-stays/setup/${siteID}/panel-users`));
	if (res.statusCode === 200) yield put(roomStaysActions.loadPanelUsersDone({ siteID, data: res.data || [] }));
	else
		yield put(
			roomStaysActions.loadPanelUsersDone({ siteID, error: errMsg(res, 'Panel users could not be loaded.') })
		);
}

/**
 * POST setup/preview or setup/apply. A preview answers every row (200); an apply answers 201 (applied), 200 (a replay
 * of the same setupKey) or 409 with the rows when a row refused. A 409 WITHOUT rows is a whole-request refusal
 * (SETUP_SITE_CONFIGURED, SETUP_KEY_CONFLICT) and is shown as an error, never as rows.
 */
export function* runSetup({ payload: { mode, body } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.post(`/room-stays/setup/${mode}`, body));
	const rows = Array.isArray(res?.data?.rows);
	const ok = mode === 'preview' ? res.statusCode === 200 : [200, 201].includes(res.statusCode);
	if ((ok || (mode === 'apply' && res.statusCode === 409)) && rows) {
		yield put(roomStaysActions.runSetupDone({ body, result: { mode, ...res.data } }));
		if (mode === 'apply' && res.data.applied) {
			Notification('success', res.data.replayed ? 'Setup already applied' : 'Site set up');
			yield put(roomStaysActions.loadHolders());
			yield put(roomStaysActions.loadSetup({ siteID: body.siteID }));
			yield put(roomStaysActions.loadBoard({ siteID: body.siteID }));
		}
		return;
	}
	const error = refusalOf(res, mode === 'preview' ? 'The preview failed.' : 'The setup was not applied.');
	// No answer (no status, or a 5xx): the apply may or may not have committed, so Apply stays enabled to resend the
	// same key. Any 4xx is an answer, coded (SETUP_KEY_CONFLICT) or not (a Joi 400, a holderAdminOnly 403), and clears it.
	const unanswered = !res?.statusCode || res.statusCode >= 500;
	yield put(
		roomStaysActions.runSetupDone({ body, result: null, error, keepPreview: mode === 'apply' && unanswered })
	);
	// The site is set up already (perhaps by the apply whose answer was lost): show the configured view.
	if (error.code === 'SETUP_SITE_CONFIGURED') yield put(roomStaysActions.loadSetup({ siteID: body.siteID }));
}

// POST /room-stays/setup/:siteID/rooms — Add room after setup (PLAN.md §5.1.9, D-16): holderAdminOnly on the server.
export function* runAddRoom({ payload: { mode, siteID, body } }) {
	// Flag off: settle the run, so the popup never stays locked on a request that was never sent.
	if (!(yield* flagOn())) {
		yield put(
			roomStaysActions.runAddRoomDone({
				siteID,
				body,
				error: { code: null, message: 'Room stays is not turned on for this organisation.' }
			})
		);
		return;
	}
	const res = yield call(() => SuperFetch.post(`/room-stays/setup/${siteID}/rooms`, { mode, ...body }));
	const answered = typeof res?.data?.ok === 'boolean';
	const ok = mode === 'preview' ? res.statusCode === 200 : [200, 201].includes(res.statusCode);
	if ((ok || (mode === 'apply' && res.statusCode === 409)) && answered) {
		yield put(roomStaysActions.runAddRoomDone({ siteID, body, result: { mode, ...res.data } }));
		if (mode === 'apply' && res.data.applied) {
			Notification('success', res.data.replayed ? 'Room already added' : 'Room added');
			yield put(roomStaysActions.loadHolders());
			yield put(roomStaysActions.loadSetup({ siteID }));
			yield put(roomStaysActions.loadBoard({ siteID }));
		} else if (mode === 'apply') {
			// Also outside the popup: it may have closed (a site change) before the answer landed.
			Notification('error', 'Room not added: it was refused. Check it again in Add room to see why.');
		}
		return;
	}
	const error = refusalOf(res, mode === 'preview' ? 'The check failed.' : 'The room was not added.');
	// As runSetup: no answer (no status, or a 5xx) may have committed, so Add room stays enabled to resend the same key.
	const unanswered = !res?.statusCode || res.statusCode >= 500;
	if (mode === 'apply')
		Notification('error', unanswered ? 'Add room got no answer. It may have been added.' : error.message);
	yield put(
		roomStaysActions.runAddRoomDone({
			siteID,
			body,
			result: null,
			error,
			keepPreview: mode === 'apply' && unanswered
		})
	);
}

// POST /room-stays/stock/:siteID/cards/:credentialID/repair — one card, within the caller's site scope (R10).
export function* repairCard({ payload: { siteID, credentialID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.post(`/room-stays/stock/${siteID}/cards/${credentialID}/repair`, {}));
	if (res.statusCode === 200)
		Notification('success', res.data?.deferred ? 'Repair queued behind a delivery in progress' : 'Repair started');
	else Notification('error', refusalOf(res, 'The card could not be repaired.').message);
	yield put(roomStaysActions.repairCardDone({ credentialID }));
	yield put(roomStaysActions.loadBoard({ siteID }));
}

// DELETE /room-stays/holders/:holderID — the server refuses a holder with cards, an unfinished stay or an open operation.
export function* retireHolder({ payload: { siteID, holderID } }) {
	if (!(yield* flagOn())) return;
	const res = yield call(() => SuperFetch.delete(`/room-stays/holders/${holderID}`));
	if (res.statusCode === 200) Notification('success', 'Room retired');
	else Notification('error', refusalOf(res, 'The room could not be retired.').message);
	yield put(roomStaysActions.retireHolderDone({ holderID }));
	yield put(roomStaysActions.loadHolders());
	yield put(roomStaysActions.loadBoard({ siteID }));
}

export default function* roomStaysSaga() {
	yield all([
		takeLatest(roomStaysActions.loadHolders.type, loadHolders),
		// Every: a reload for one room must not cancel another's, and each stay change keeps its own toast (DB2-02, DB2-06).
		takeEvery(roomStaysActions.loadStays.type, loadStays),
		takeEvery(roomStaysActions.changeStay.type, changeStay),
		takeEvery(roomStaysActions.createStay.type, createStay),
		// Every, never latest: cancelling a run between two cards would leave the first issued and the second never sent.
		takeEvery(roomStaysActions.issueForStay.type, issueForStay),
		// One channel for both board loads: a newer read (a repair's or retire's own site, or the page's reload) cancels
		// an older one, so a slower, older answer can never overwrite a newer board.
		takeLatest([roomStaysActions.loadBoard.type, roomStaysActions.loadBoards.type], loadAnyBoard),
		// Every: the Rooms tab reads each shown site's setup (its doors); latest would cancel all but the last site's.
		takeEvery(roomStaysActions.loadSetup.type, loadSetup),
		takeLatest(roomStaysActions.loadOccupancy.type, loadOccupancy),
		takeLatest(roomStaysActions.readOccupancy.type, readOccupancy),
		takeLatest(roomStaysActions.loadPanelUsers.type, loadPanelUsers),
		// Every, never latest: cancelling a running apply would drop its answer while the server had already applied it.
		takeEvery(roomStaysActions.runSetup.type, runSetup),
		takeEvery(roomStaysActions.runAddRoom.type, runAddRoom),
		// Every: two cards repaired or two rooms retired in a row must each run.
		takeEvery(roomStaysActions.repairCard.type, repairCard),
		takeEvery(roomStaysActions.retireHolder.type, retireHolder)
	]);
}
